import { AnyAction } from '@reduxjs/toolkit'
import {
  initialState,
  MsftCombinedFormState,
  MsftCombinedFormStatus,
} from './reducer'
import { AppDispatch, RootState } from '../../../store'
import { startLoading, stopLoading, LoadingTypes } from '@labrav/common'
import { updateAppAlert } from '../../../common/modules/appAlert/actions'
import { AxiosError, AxiosResponse } from 'axios'
import { getErrorMessages } from '../../../common/utils/error'
import { RequestFailureMessage } from '../../../common/utils/messagesContants'
import { errorLogger } from '../../../common/utils/errorLogger'
import { camelize, snakeize } from 'casing'
import {
  getMicrosoftCombinedForm,
  putMicrosoftCombinedForm,
} from '../../api/azure'
import {
  AzurePricingType,
  RecurrentPriceModeType,
} from '../privateOffer/reducer'
import {
  getAzureMappedPlans,
  processMeters,
} from '../resaleAuthorization/actions'
import { Product } from '../productsListing/reducer'
import { fetchSellerDataMicrosoft } from '../../api/markeplace'

export enum MsftCombineFormActionTypes {
  SET_COMBINED_FORM_DATA = 'SET_COMBINED_FORM_DATA',
  CLEAR_COMBINED_FORM_DATA = 'CLEAR_COMBINED_FORM_DATA',
}

export const setCombinedFormData = (data: MsftCombinedFormState) => {
  return {
    type: MsftCombineFormActionTypes.SET_COMBINED_FORM_DATA,
    payload: data,
  }
}
export const clearCombinedFormData = () =>
  ({
    type: MsftCombineFormActionTypes.CLEAR_COMBINED_FORM_DATA,
  }) as unknown as AnyAction

export const getTransformedCombinedFormData = ({
  data,
  products,
}: {
  data: Record<string, any>
  products: Product[]
}) => {
  const { draft_offer, ...remainingForm } = data
  const remainingFormData = camelize(remainingForm)
  const { plans, ...remainingDraftOffer } = draft_offer
  const camelizedDraftOffer = camelize(remainingDraftOffer)
  const product =
    products?.find(
      product => product.productId === camelizedDraftOffer?.labraProductId
    ) || ({} as Product)

  return {
    ...remainingFormData,
    draftOffer: {
      ...camelizedDraftOffer,
      acceptBy: camelizedDraftOffer?.acceptBy || null,
      offerExpirationDate: camelizedDraftOffer?.offerExpirationDate || null,
      offerStartDate: camelizedDraftOffer?.offerStartDate || null,
      plans: plans?.length
        ? getAzureMappedPlans({ plans, product, isBuyerPrice: true })
        : [
            {
              displayName: '',
              planId: '',
              offerPricingType: 'saasNewCustomizedPlans',
              description: '',
              labraPlanId: '',
              discountPercentage: null,
              buyerPrice: null,
              discountType: AzurePricingType.ABSOLUTE,
              markupPercentage: null,
              pricing: {
                recurrentPrice: {
                  recurrentPriceMode: '',
                  priceInputOption: 'usd',
                  prices: [],
                },
              },
            },
          ],
    },
    labraResellerId: remainingFormData?.labraResellerId,
    labraIsvId: remainingFormData?.labraIsvId,
    id: remainingFormData?.id,
  }
}
export const getMicrosoftCombinedFormData =
  ({
    formId,
    labraProductId,
    cloudProductId,
  }: {
    formId: string
    labraProductId: string
    cloudProductId: string
  }) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    dispatch(startLoading(LoadingTypes.COMBINED_FORM_LOADING))
    const { AZURE } = getState().productsListing
    try {
      const { data } = await getMicrosoftCombinedForm({
        formId,
      })
      await dispatch(
        setCombinedFormData(
          data?.draft_offer
            ? getTransformedCombinedFormData({
                data,
                products: AZURE?.products?.rows,
              })
            : camelize({
                ...data,
                draft_Offer: {
                  ...initialState.draftOffer,
                  labraProductId,
                  cloudProductId,
                },
              })
        )
      )
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>
      dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(errorResponse),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
    } finally {
      dispatch(stopLoading(LoadingTypes.COMBINED_FORM_LOADING))
    }
  }

export const submitCombinedFormData =
  ({
    combinedFormData,
    formId,
    isvCloudAccountId,
    handleSuccess,
    externalMetadata,
  }: {
    combinedFormData: MsftCombinedFormState
    formId: string
    isvCloudAccountId: string
    handleSuccess: () => void
    externalMetadata: Record<string, unknown>
  }) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    dispatch(startLoading(LoadingTypes.SUBMIT_COMBINED_FORM))
    const { AZURE } = getState().productsListing
    try {
      const { id, draftOffer, ...restFormData } = combinedFormData
      const {
        resellerEulaFiles,
        plans,
        offerStartDate,
        offerExpirationDate,
        acceptBy,
        labraProductId,
        cloudProductId,
        ...restDraftOffer
      } = draftOffer

      const payloadPlans = plans
        ?.filter(item => item?.labraPlanId)
        .map(({ originalPricing, pricing, buyerPrice, ...remainingPlan }) => {
          const isItPerUser =
            pricing?.recurrentPrice?.recurrentPriceMode ===
            RecurrentPriceModeType.PERUSER

          const { userLimits, ...remainingRecurrentPrice } =
            pricing?.recurrentPrice

          const hasInstallmentSchedules =
            (pricing?.recurrentPrice?.prices?.[0]?.installments?.schedule
              ?.length ?? 0) > 0

          return {
            ...snakeize(remainingPlan),
            pricing: {
              recurrent_price: snakeize({
                ...remainingRecurrentPrice,
                ...(isItPerUser ? { userLimits } : {}),
                price_input_option: 'usd',
                ...(hasInstallmentSchedules
                  ? {
                      prices: [
                        {
                          ...remainingRecurrentPrice?.prices?.[0],
                          paymentOption: {
                            ...remainingRecurrentPrice?.prices?.[0]
                              ?.paymentOption,
                            type: 'flexible',
                          },
                          pricePerPaymentInUsd: undefined,
                        },
                      ],
                    }
                  : {}),
              }),
              custom_meters: {
                ...pricing?.customMeters,
                meters: processMeters(pricing?.customMeters?.meters || {}),
                price_input_option: 'usd',
              },
            },
          }
        })

      const cleanedPayload = {
        ...snakeize({
          ...restFormData,
          labraIsvId: isvCloudAccountId,
          externalMetadata: externalMetadata,
        }),
        draft_offer: {
          ...snakeize({
            ...restDraftOffer,
            plans: payloadPlans?.length ? [...payloadPlans] : [],
            resellerEulaFiles,
            labraProductId: labraProductId,
            cloudProductId: cloudProductId,
            offerExpirationDate: offerExpirationDate || null,
            acceptBy: acceptBy || undefined,
            offerStartDate: offerStartDate || null,
          }),
          plans: payloadPlans?.length ? [...payloadPlans] : [],
        },
      }

      const { data } = await putMicrosoftCombinedForm({
        data: cleanedPayload,
        formId,
      })
      dispatch(
        updateAppAlert({
          message: 'Offer saved successfully',
          messageType: 'SUCCESS',
          autoClose: true,
        })
      )
      if (data?.draft_offer) {
        dispatch(
          setCombinedFormData(
            getTransformedCombinedFormData({
              data,
              products: AZURE?.products?.rows,
            })
          )
        )
      } else {
        dispatch(
          setCombinedFormData(
            camelize({
              ...data,
              draftOffer: camelize(initialState.draftOffer),
            })
          )
        )
      }
      if (handleSuccess) {
        handleSuccess()
      }
    } catch (error: any) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>

      dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(errorResponse),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
    } finally {
      dispatch(stopLoading(LoadingTypes.SUBMIT_COMBINED_FORM))
    }
  }

export const getSelectedAccountDetail =
  ({ accountId }: { accountId: string }) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    try {
      const { data } = await fetchSellerDataMicrosoft({
        accountId,
      })
      return data?.mpn_id
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>

      dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(errorResponse),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
    }
  }
